<script lang="ts">
    import { onDestroy, onMount } from 'svelte';
    import { theme } from '../stores/theme';

    // ── Types ─────────────────────────────────────────────────────────────────

    type Point = { t: number; v: number };
    type Series = { label: string; color: string; points: Point[] };

    // ── Color generation ──────────────────────────────────────────────────────
    
    // Material Design 500 (Light) and 200 (Dark) palettes
    const MATERIAL_COLORS = {
        light: ['#2196F3', '#4CAF50', '#9C27B0', '#FF9800', '#E91E63', '#009688', '#F44336'],
        dark:  ['#90CAF9', '#A5D6A7', '#CE93D8', '#FFCC80', '#F48FB1', '#80CBC4', '#EF9A9A']
    };

    function getSeriesColor(index: number, currentTheme: string): string {
        const palette = currentTheme === 'dark' ? MATERIAL_COLORS.dark : MATERIAL_COLORS.light;
        return palette[index % palette.length];
    }

    // ── Mock data ─────────────────────────────────────────────────────────────

    let now = Date.now();
    let lastUpdate = Date.now();
    const WINDOW_MS = 24 * 60 * 60 * 1000; // 24h window
    const INITIAL_STEP = 30 * 60 * 1000; // 30 min initial step
    const sectors = ['Sector A', 'Sector B', 'Sector C', 'Sector D', 'Sector E'];

    function generateInitialSeries(base: number, noise: number, drift: number): Point[] {
        let v = base;
        return Array.from({ length: 49 }, (_, i) => {
            v += (Math.random() - 0.5) * noise + drift * Math.sin(i / 6);
            return { t: now - (48 - i) * INITIAL_STEP, v: Math.round(v * 10) / 10 };
        });
    }

    let series: Series[] = sectors.map((label, i) => ({
        label,
        color: getSeriesColor(i, $theme),
        points: generateInitialSeries(30 + i * 12, 3 + i, 4 + i),
    }));

    $: {
        series = series.map((s, i) => ({
            ...s,
            color: getSeriesColor(i, $theme)
        }));
    }

    // ── Updates ───────────────────────────────────────────────────────────────

    let tickCounter = 0;
    const tickInterval = setInterval(() => {
        tickCounter++;
        now = Date.now(); 
    }, 1000);

    const dataInterval = setInterval(() => {
        const t = Date.now();
        lastUpdate = t;

        series = series.map((s, i) => {
            const lastVal = s.points[s.points.length - 1].v;
            const noise = 3 + i; 
            const change = (Math.random() - 0.5) * noise;
            let newVal = Math.round((lastVal + change) * 10) / 10;
            
            if (newVal < 10) newVal += 1;
            if (newVal > 90) newVal -= 1;

            // Add new point, filter out points older than 24h + some buffer
            const newPoints = [...s.points, { t, v: newVal }].filter(p => p.t > t - WINDOW_MS - INITIAL_STEP);
            return { ...s, points: newPoints };
        });
    }, 20000);

    onDestroy(() => {
        clearInterval(tickInterval);
        clearInterval(dataInterval);
    });

    // ── Chart geometry ────────────────────────────────────────────────────────

    const W = 800, H = 220; // Slightly taller
    const PAD = { top: 20, right: 64, bottom: 40, left: 16 }; // Increased right for labels, top/bottom for breathing room
    const innerW = W - PAD.left - PAD.right;
    const innerH = H - PAD.top  - PAD.bottom;

    $: allVals = series.flatMap(s => s.points.map(p => p.v));
    $: minV    = Math.floor(Math.min(...allVals) - 4); // More buffer
    $: maxV    = Math.ceil (Math.max(...allVals) + 4); // More buffer

    // FIXED sliding window for X axis
    $: maxTime = now;
    $: minTime = now - WINDOW_MS;
    
    $: xScale = (t: number) => {
        const range = maxTime - minTime;
        if (range === 0) return PAD.left;
        return PAD.left + ((t - minTime) / range) * innerW;
    };

    $: yScale = (v: number) => {
        const range = maxV - minV;
        if (range === 0) return PAD.top + innerH;
        return PAD.top + innerH - ((v - minV) / range) * innerH;
    };

    $: toPath = (pts: Point[]) =>
        pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${xScale(p.t).toFixed(1)},${yScale(p.v).toFixed(1)}`).join(' ');

    // ── Ticks ─────────────────────────────────────────────────────────────────

    $: yTicks = (() => {
        const ticks: number[] = [];
        const tickStep = Math.ceil((maxV - minV) / 5) || 1;
        for (let v = Math.ceil(minV / tickStep) * tickStep; v <= maxV; v += tickStep) {
            ticks.push(v);
        }
        return ticks;
    })();

    $: xTicks = (() => {
        const ticks: number[] = [];
        const start4h = Math.ceil(minTime / (4 * 3600000)) * (4 * 3600000);
        for (let t = start4h; t <= maxTime; t += 4 * 3600000) {
            if (t >= minTime) ticks.push(t);
        }
        return ticks;
    })();

    const fmtTime = (t: number) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const fmtFullTime = (t: number) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

    function formatAgo(t: number, _now: number): string {
        const diff = Math.floor((_now - t) / 1000);
        if (diff < 60) return `${diff}s ago`;
        const m = Math.floor(diff / 60);
        if (m < 60) return `${m} min ago`;
        const h = Math.floor(m / 60);
        return `${h} h ago`;
    }

    // ── Interaction ───────────────────────────────────────────────────────────

    let hoveredIndex: number | null = null;
    let hoveredSeriesIndex: number | null = null;
    let hoveredX = 0;

    function onMouseMove(e: MouseEvent) {
        const svg   = (e.currentTarget as SVGSVGElement);
        const rect  = svg.getBoundingClientRect();
        const svgX  = ((e.clientX - rect.left) / rect.width)  * W;
        
        if (svgX < PAD.left || svgX > W - PAD.right) {
            hoveredIndex = null;
            return;
        }

        const frac = (svgX - PAD.left) / innerW;
        const targetT = minTime + frac * (maxTime - minTime);
        
        // Find closest point in time across all series (assuming they share timestamps mostly)
        const pts = series[0].points;
        let bestIdx = 0;
        let minDiff = Infinity;
        for (let i = 0; i < pts.length; i++) {
            const diff = Math.abs(pts[i].t - targetT);
            if (diff < minDiff) {
                minDiff = diff;
                bestIdx = i;
            }
        }
        
        hoveredIndex = bestIdx;
        hoveredX = xScale(pts[bestIdx].t);
    }

    function onMouseLeave() {
        hoveredIndex = null;
    }

    // ── Display Data ──────────────────────────────────────────────────────────

    $: displayData = (() => {
        if (hoveredIndex !== null && series[0].points[hoveredIndex]) {
            const t = series[0].points[hoveredIndex].t;
            return {
                isLive: false,
                badge: 'History',
                timeLabel: fmtFullTime(t),
                agoLabel: formatAgo(t, now),
                values: series.map((s, i) => ({
                    index: i,
                    label: s.label,
                    color: s.color,
                    v: s.points[hoveredIndex!]?.v ?? 0
                }))
            };
        } else {
            const latestIdx = series[0].points.length - 1;
            return {
                isLive: true,
                badge: 'Live',
                timeLabel: 'Updated',
                agoLabel: formatAgo(lastUpdate, now),
                values: series.map((s, i) => ({
                    index: i,
                    label: s.label,
                    color: s.color,
                    v: s.points[latestIdx]?.v ?? 0
                }))
            };
        }
    })();

</script>

<div class="chart-container">
    <div class="chart-area">
        <svg
            viewBox="0 0 {W} {H}"
            preserveAspectRatio="none"
            class="chart-svg"
            on:mousemove={onMouseMove}
            on:mouseleave={onMouseLeave}
            role="img"
            aria-label="Temperature history chart"
        >
            <!-- Background Grid (Horizontal light solid lines) -->
            {#each yTicks as v}
                <line
                    x1={PAD.left} x2={W - PAD.right}
                    y1={yScale(v)} y2={yScale(v)}
                    class="grid-line"
                />
                <!-- Y-Axis Labels: Right side of the graph, left aligned with units -->
                <text x={W - PAD.right + 10} y={yScale(v)} class="axis-label" text-anchor="start" dominant-baseline="middle">
                    {v.toFixed(0)} °C
                </text>
            {/each}

            {#each xTicks as t}
                <line 
                    x1={xScale(t)} x2={xScale(t)} 
                    y1={H - PAD.bottom} y2={H - PAD.bottom + 6} 
                    class="axis-line" 
                />
                <text x={xScale(t)} y={H - PAD.bottom + 18} class="axis-label" text-anchor="middle">
                    {fmtTime(t)}
                </text>
            {/each}

            <!-- Series lines -->
            {#each series as s, i}
                <path 
                    d={toPath(s.points)} 
                    fill="none" 
                    style="stroke:{s.color}; opacity:{hoveredSeriesIndex === null || hoveredSeriesIndex === i ? 1 : 0.15}" 
                    stroke-width={hoveredSeriesIndex === i ? 4 : 2} 
                    stroke-linejoin="round" 
                    stroke-linecap="round" 
                    class="series-path"
                />
            {/each}

            <!-- Hover Interaction -->
            {#if hoveredIndex !== null}
                <line x1={hoveredX} x2={hoveredX} y1={PAD.top} y2={H - PAD.bottom} class="crosshair" />
                {#each displayData.values as item, i}
                    <circle 
                        cx={hoveredX} 
                        cy={yScale(item.v)} 
                        r={hoveredSeriesIndex === i ? 6 : 4} 
                        style="fill:{item.color};stroke:var(--color-bg-light); opacity:{hoveredSeriesIndex === null || hoveredSeriesIndex === i ? 1 : 0.15}" 
                        stroke-width="2" 
                    />
                {/each}
            {/if}
        </svg>
    </div>

    <!-- Modern Info Bar -->
    <div class="info-bar">
        <div class="status-col">
            <div class="badge-row">
                <span class="badge badge--{displayData.isLive ? 'live' : 'history'}">
                    {displayData.badge}
                </span>
            </div>
            <div class="timestamp">
                {displayData.timeLabel} 
                <span class="ago">{displayData.agoLabel}</span>
            </div>
        </div>
        
        <div class="values-list">
            {#each displayData.values as item}
                <div 
                    class="value-item" 
                    on:mouseenter={() => hoveredSeriesIndex = item.index}
                    on:mouseleave={() => hoveredSeriesIndex = null}
                    style="opacity:{hoveredSeriesIndex === null || hoveredSeriesIndex === item.index ? 1 : 0.4}"
                >
                    <span class="dot" style="background-color: {item.color}"></span>
                    <span class="label">{item.label}</span>
                    <span class="val">{item.v.toFixed(1)}°C</span>
                </div>
            {/each}
        </div>
    </div>
</div>

<style lang="scss">
    .chart-container {
        display: flex;
        flex-direction: column;
        gap: 1.25em;
    }

    .chart-area {
        position: relative;
    }

    .info-bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 2em;
        padding: 1em 1.25em;
        background-color: $color-bg-offset; // Use offset color instead of secondary
        border-radius: 12px;

        :global([data-theme="dark"]) & {
            background-color: rgba(255, 255, 255, 0.03); // Very subtle lift in dark mode
        }
    }

    .status-col {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 0.4em;
        flex-shrink: 0;
    }

    .badge-row {
        line-height: 1;
    }

    .badge {
        display: inline-block;
        padding: 0.25em 0.75em;
        border-radius: 100px;
        font-size: 0.68em;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        background-color: $color-bg-active; // Unified background

        &--live {
            color: $color-success;
            position: relative;
            padding-left: 1.8em;

            &::before {
                content: '';
                position: absolute;
                left: 0.7em;
                top: 50%;
                transform: translateY(-50%);
                width: 6px;
                height: 6px;
                border-radius: 50%;
                background-color: currentColor;
                animation: pulse 2s infinite;
            }
        }

        &--history {
            color: $color-text-secondary;
        }
    }

    .timestamp {
        font-size: 0.82em;
        color: $color-text;
        font-weight: 500;
        white-space: nowrap;
        min-width: 140px; 

        .ago {
            color: $color-disabled-text;
            font-weight: 400;
            margin-left: 0.25em;
            font-feature-settings: "tnum";
        }
    }

    .values-list {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
        gap: 0.75em 1.5em;
        flex: 1;
        justify-content: end;
    }

    .value-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.5em;
        font-size: 0.82em;
        min-width: 0;
        cursor: pointer;
        transition: opacity 0.2s ease;
    }

    .dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        flex-shrink: 0;
    }

    .label {
        color: $color-disabled-text;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        flex: 1;
    }

    .val {
        font-weight: 600;
        color: $color-text;
        font-variant-numeric: tabular-nums;
        flex-shrink: 0;
        min-width: 4.5ch;
        text-align: right;
    }

    @keyframes pulse {
        0% { opacity: 1; transform: translateY(-50%) scale(1); }
        50% { opacity: 0.3; transform: translateY(-50%) scale(0.8); }
        100% { opacity: 1; transform: translateY(-50%) scale(1); }
    }

    .chart-svg {
        display: block;
        width: 100%;
        height: auto;
        overflow: visible;
        cursor: crosshair;
    }

    .grid-line {
        stroke: $color-border;
        stroke-width: 1;
        opacity: 0.4; // Very light solid lines
    }

    .axis-line {
        stroke: $color-border;
        stroke-width: 1;
    }

    .axis-label {
        fill: $color-disabled-text;
        font-size: 11px;
        font-family: inherit;
        font-weight: 400;
    }

    .crosshair {
        stroke: $color-text-secondary;
        stroke-width: 1;
        stroke-dasharray: 3 3;
    }

    .series-path {
        transition: opacity 0.2s ease, stroke-width 0.2s ease;
    }
</style>
